import { t, jt } from 'ttag'
import styled from 'styled-components'
import { useState, useMemo, useCallback, useContext } from 'react'
import { useMutation } from 'react-query'
import { useDispatch } from 'react-redux'
import PropTypes from 'prop-types'

import { api } from '@h2/api'
import { useForm } from '@h2/common/components/hooks/useForm'
import { useNavigator } from '@h2/common/components/hooks/useNavigator'
import { Link as LinkBase } from '@h2/common/components/link'
import { userForgotCredential } from '@h2/common/actions/userForgotCredential'
import { Text } from '@h2/components/typography/text'
import { Alert } from '@h2/components/display/alert'
import { Field } from '@h2/components/inputs/field'
import { TextInput } from '@h2/components/inputs/text'
import { Button } from '@h2/components/buttons/button'
import { Flex } from '@h2/components/layout/flex'
import { Globals } from '@h2/globals'
import { Back } from '@h2/common/components/registration/back'
import { features } from '@h2/components/media'
import { BLACK } from '@h2/components/colors'

const NavianceText = styled(Text)`
text-align: center;
font-size: 14px;
padding: 5px 20px;
`
const ButtonLink = styled(Button)`
width: 235px;
height: 38px;
margin-left: auto;
margin-top: 0px;
@media (${features.maxWidth.laptop}) {
  margin-left: inherit;
  margin-top: 15px;
}
`
const FlexDirection = styled(Flex)`
margin-top: 115.6px;
@media (${features.maxWidth.laptop}) {
  flex-direction: column;
  align-items: center;
}
@media (${features.maxWidth.widescreen}) {
  margin-top: 50px;
}
`
const Form = ({ onClickLink }) => {
  const dispatch = useDispatch()
  const navigate = useNavigator()
  const [response, setResponse] = useState(null)
  const { isNavianceUser } = useContext(Globals)
  const { register, handleOnSubmit, error, isInvalid } = useForm({
    username(form) {
      if (!form.username?.trim()) {
        return t`Username is required`
      }
    }
  })
  const { mutate, isLoading } = useMutation(
    body =>
      api.fetch('/user/request/passwordreset', {
        version: 'v1',
        method: 'POST',
        body: JSON.stringify(body)
      }),
    {
      onSuccess: data => {
        if (data.success) {
          setResponse({ type: 'success', message: data.success })
        }

        if (data.error) {
          setResponse({ type: 'error', message: data.error })
        }
      },
      onError: () => {
        setResponse({
          type: 'error',
          message: t`An error occured, please try again later.`
        })
      }
    }
  )
  const link = useMemo(
    () => (
      <LinkBase
        key="jt-key"
        href="/lookupuser"
        fontSize={isNavianceUser ? '14px' : '16px'}
        weight={isNavianceUser ? 'bold' : 'normal'}
        color={isNavianceUser ? '#57AD65' : BLACK}
        textDecoration={isNavianceUser ? 'underline' : 'none'}
        onClick={evt => {
          evt.preventDefault()

          if (typeof onClickLink === 'function') {
            onClickLink(evt)
          } else {
            navigate(evt.target.pathname)
          }
        }}>
        {isNavianceUser ? t`here` : t`you can look it up here`}
      </LinkBase>
    ),
    [onClickLink, navigate, isNavianceUser]
  )
  const onClickCancel = useCallback(() => {
    if (typeof onClickLink === 'function') {
      dispatch(userForgotCredential(''))
    } else {
      navigate('/signin')
    }
  }, [onClickLink, dispatch, navigate])

  return (
    <form onSubmit={handleOnSubmit(form => mutate(form))}>
      {isNavianceUser ? (
        <>
          <NavianceText style={{margin: '0px'}}>
            {t`Enter the username you selected when registering for an account, then click Reset Password. If you have an email address on file, you’ll receive an email with further instructions. You cannot reset your password if you do not have an email address on file.`}
          </NavianceText>
          <NavianceText>
            {jt`To find the username you selected, look it up ${link}.`}
          </NavianceText>
        </>
      ) : (
        <Text>
          {jt`In the box below, enter the username you selected when you registered for an account. When you have done that, click "Reset Password" and an email with further instructions will be sent to the email address on file. If you did not enter an email address when you created your account, then there is no way to reset your account. If you can't remember the username you selected, ${link}.`}
        </Text>
      )}
        {response && <Alert {...response} onClose={() => setResponse(null)} />}
        <Field
          isRequired
          label={t`Username or email`}
          status={error.username ? 'error' : 'default'}
          desc={error.username}
          margin="20px 0">
          <TextInput {...register('username', { required: true })} />
        </Field>
      {!isNavianceUser ? (
        <Flex gap="15px">
          <Button
            type="button"
            size="large"
            variant="outlined"
            isDisabled={isLoading}
            onClick={onClickCancel}>
            {t`Cancel`}
          </Button>
          <Button
            type="submit"
            size="large"
            isDisabled={isInvalid || isLoading}
            isLoading={isLoading}>
            {t`Reset Password`}
          </Button>
        </Flex>
      ) : (
        <FlexDirection>
          <Back onClick={onClickCancel} />
          <ButtonLink
            type="submit"
            size="large"
            align="center"
            isDisabled={isInvalid || isLoading}
            isLoading={isLoading}>
            {t`Reset Password`}
          </ButtonLink>
        </FlexDirection>
      )}
    </form>
  )
}

Form.propTypes = {
  /** Callback when clicking link within text. Only defined when the form is rendered inside a modal dialog. */
  onClickLink: PropTypes.func
}

export { Form }
